/* eslint-disable no-underscore-dangle */
import React from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { ServiceCategory } from '@prisma/client';
import FormSmallModal, {
  FormData,
  InputRow,
} from '../../global/FormSmallModal';
import {
  BusinessService,
  SettingsRow,
  UpsertBusinessServiceInitValues,
  upsertBusinessServiceSchema,
} from '../../../types/businesses';
import {
  getBusinessCurrency,
  getBusinessShopUrl,
  handleUpsertBusinessService,
} from '../../../helpers/businesses';
import { LoadedUser } from '../../../interfaces';
import SettingsSection from '../SettingsSection';
import useGetBusinessServices from '../../../hooks/businesses/useGetBusinessServices';
import useUser from '../../../hooks/global/useUser';
import useGetWaivers from '../../../hooks/waivers/useGetWaivers';
import useGetOnboarding from '../../../hooks/businesses/useGetOnboarding';
import SettingsGridRow, { SettingsGridRowCell } from '../SettingsGridRow';
import useConfirm from '../../../hooks/useConfirm';
import usePiercingLocations from '../../../hooks/piercings/usePiercingLocations';
import { postMessageToReactNative } from '../../../utils/global/helper';

const TOOLTIP: Record<string, string> = {
  name: 'Service name will be visible to customers.',
  description: 'Service description will be visible to customers.',
  defaultPrice:
    'You can always change the price of each individual appointment but this will give clients an estimate of where prices start at for this service.',
  defaultDepositPrice:
    'This is the default deposit price for this service. You can always change the deposit price of each individual appointment.',
  defaultDurationInMinutes:
    'This is the default duration for this service. You can always change the duration of each individual appointment. Default duration must be in increments of 5 minutes.',
  enableDirectBookings:
    'Do you want to let customers schedule this service from your shop booking page?',
  aftercareLink:
    'Add an aftercare link to send customers after an appointment for this service is completed',
  serviceCategory:
    'This can only be changed as long as no appointments have been created for this service.',
};

function ServiceRow({ service }: { service: BusinessService }) {
  const { showWaiversFeatures, data } = useGetWaivers();

  const defaultWaiverName = data?.options?.find(waiver => waiver.isDefault)
    ?.waiverName;

  const waiverName = showWaiversFeatures
    ? service?.waiver?.name || defaultWaiverName || 'No waiver'
    : null;

  return (
    <SettingsGridRow
      cells={
        [
          {
            id: 'name',
            text: service?.serviceName || 'Unnamed service',
          },
          {
            id: 'employees',
            text: `${
              service?.employeesToServices?.length || 0
            } service providers`,
          },
          showWaiversFeatures && {
            id: 'waiver',
            text: waiverName,
          },
        ].filter(Boolean) as SettingsGridRowCell[]
      }
    />
  );
}

function ServicesSettings() {
  const confirm = useConfirm();
  const { user } = useUser<LoadedUser>();
  const {
    services = [],
    isLoading,
    isEnabled,
    refetch,
  } = useGetBusinessServices();
  const { showWaiversFeatures, data: waiversData } = useGetWaivers();
  const { reloadAfterUpdate } = useGetOnboarding();
  const [form, setForm] = React.useState<FormData | null>(null);
  const availablePiercingLocations = usePiercingLocations() ?? [];
  const theme = useTheme();

  if (!isEnabled) return null;

  const piercingLocationsOptions = availablePiercingLocations?.map(
    location => ({
      label: location.locationType,
      value: location.locationId,
      category: location.locationCategory,
    }),
  );

  const getServiceForm = (
    serviceToUpdate?: BusinessService | null,
  ): FormData<UpsertBusinessServiceInitValues> => {
    const waiverOptions = waiversData?.options?.filter(
      option => option.status === 'DRAFT',
    );
    const defaultWaiver = waiverOptions?.find(option => option.isDefault);
    const isOnlyDefaultWaiver = waiverOptions?.length === 1 && !!defaultWaiver;
    const waiverName =
      serviceToUpdate?.waiver?.name ?? defaultWaiver?.waiverName;

    const minimumAgeInMonths = serviceToUpdate?.minimumAgeInMonths ?? 0;
    const isYears = minimumAgeInMonths % 12 === 0;
    const unit = isYears || !minimumAgeInMonths ? 'years' : 'months';
    const minimumAge =
      unit === 'months'
        ? minimumAgeInMonths
        : parseInt((minimumAgeInMonths / 12).toString(), 10);

    const isExistingServicePiercing =
      serviceToUpdate?.serviceCategory === ServiceCategory.PIERCING;
    const piercingLocations = isExistingServicePiercing
      ? (serviceToUpdate?.piercingLocations ?? [])?.map(pl => pl.locationId)
      : [];
    const initialValues: UpsertBusinessServiceInitValues = {
      serviceCategory: (serviceToUpdate?.serviceCategory ||
        '') as ServiceCategory,
      name: serviceToUpdate?.serviceName || '',
      description: serviceToUpdate?.serviceDescription || '',
      defaultDurationInMinutes: serviceToUpdate?.defaultDurationInMinutes || 0,
      defaultPrice: serviceToUpdate?.defaultPrice || 0,
      defaultDepositPrice: serviceToUpdate?.defaultDepositPrice || 0,
      employeeIds:
        serviceToUpdate?.employeesToServices?.map(e => e.employeeId) || [],
      enableDirectBookings: serviceToUpdate?.enableDirectBookings as boolean,
      aftercareLink: serviceToUpdate?.aftercareLink || '',
      minimumAge,
      minimumAgeUnit: unit,
      isGeneralPiercings: (serviceToUpdate
        ? isExistingServicePiercing && piercingLocations.length > 1
        : undefined) as boolean,
      waiverName: waiverName || '',
      piercingLocations,
    };

    return {
      initialValues,
      inputRows: (values, { setFieldValue }) => {
        const isPiercing = values.serviceCategory === ServiceCategory.PIERCING;
        const piercingRows: InputRow<UpsertBusinessServiceInitValues>[] =
          isPiercing
            ? [
                [
                  {
                    inputType: 'select',
                    label:
                      'Is this a general piercing service, or specific to an individual piercing location?',
                    isLabelMultiline: true,
                    name: 'isGeneralPiercings',
                    tooltip: TOOLTIP.isGeneralPiercings,
                    placeholder: 'Select an option',
                    options: [
                      { label: 'General piercing service', value: true },
                      {
                        label: 'Specific to an individual piercing location',
                        value: false,
                      },
                    ],
                    required: true,
                    autoWidth: false,
                    onChange: newIsGeneralPiercings => {
                      const newPiercingLocations = newIsGeneralPiercings
                        ? piercingLocationsOptions.map(option => option.value)
                        : [];
                      setFieldValue('piercingLocations', newPiercingLocations);
                    },
                  },
                ],
                typeof values.isGeneralPiercings !== 'boolean'
                  ? []
                  : values.isGeneralPiercings
                  ? [
                      {
                        inputType: 'multi-checkbox',
                        label:
                          'Select which piercing locations you offer for this service:',
                        name: 'piercingLocations',
                        options: piercingLocationsOptions,
                        required: true,
                        isLabelMultiline: true,
                      },
                    ]
                  : [
                      {
                        inputType: 'category-select',
                        label: 'Piercing Location',
                        name: 'piercingLocations[0]' as keyof UpsertBusinessServiceInitValues,
                        options: piercingLocationsOptions,
                        autoWidth: false,
                        placeholder: 'Select piercing location',
                        required: true,
                        p: 1,
                        selectedBackgroundColor: 'rgba(255, 48, 8, 0.08)',
                        selectedCheckColor: theme.palette.primary.main,
                      },
                    ],
              ]
            : [];

        const inputRows: InputRow<UpsertBusinessServiceInitValues>[] = [
          [
            {
              inputType: 'select',
              label: 'Service Category',
              name: 'serviceCategory',
              options: [
                { label: 'Piercing', value: ServiceCategory.PIERCING },
                {
                  label: 'Permanent Makeup',
                  value: ServiceCategory.PERMANENT_MAKEUP,
                },
                { label: 'Other', value: ServiceCategory.OTHER },
              ],
              autoWidth: false,
              disabled: !!serviceToUpdate?._count?.appointments,
              tooltip: TOOLTIP.serviceCategory,
              placeholder: 'Select service category',
              required: true,
            },
          ],
          [
            {
              inputType: 'text',
              label: 'Service Name',
              name: 'name',
              placeholder: 'Enter service name',
              tooltip: TOOLTIP.name,
              required: true,
            },
          ],
          ...piercingRows,
          [
            {
              inputType: 'textarea',
              label: 'Service Description',
              name: 'description',
              placeholder: 'Enter service description',
              tooltip: TOOLTIP.description,
              required: true,
            },
          ],
          [
            {
              inputType: 'multiSelect',
              label: 'Service Providers',
              name: 'employeeIds',
              allText: 'All users',
              options: user?.business?.employees?.map(e => ({
                label: e.displayName || 'Unnamed service provider',
                value: e.employeeId,
              })),
              placeholder: 'Select service providers',
              autoWidth: false,
            },
          ],
          [
            {
              inputType: 'number',
              label: 'Default Duration',
              name: 'defaultDurationInMinutes',
              placeholder: 'Enter service duration',
              suffix: 'minutes',
              tooltip: TOOLTIP.defaultDurationInMinutes,
              min: 5,
              required: true,
            },
            {
              inputType: 'number',
              label: 'Default Price',
              name: 'defaultPrice',
              placeholder: 'Enter service price',
              suffix: getBusinessCurrency(user?.business),
              tooltip: TOOLTIP.defaultPrice,
              min: 0,
              step: 0.01,
              required: true,
            },
          ],
          [
            {
              inputType: 'number',
              label: 'Default Deposit Price',
              name: 'defaultDepositPrice',
              placeholder: 'Enter deposit price',
              suffix: getBusinessCurrency(user?.business),
              tooltip: TOOLTIP.defaultDepositPrice,
              min: 0,
              step: 0.01,
            },
          ],
          [
            {
              inputType: 'select',
              label: 'Enable Online Bookings',
              name: 'enableDirectBookings',
              options: [
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ],
              autoWidth: false,
              tooltip: TOOLTIP.enableDirectBookings,
              placeholder: 'Select an option',
              required: true,
            },
          ],
          [
            {
              inputType: 'select',
              label: 'Waiver',
              name: 'waiverName',
              isVisible:
                showWaiversFeatures &&
                !!waiverOptions?.length &&
                !isOnlyDefaultWaiver,
              options:
                waiverOptions?.map(waiver => ({
                  label: waiver.waiverName,
                  value: waiver.waiverName,
                })) ?? [],
              autoWidth: false,
              placeholder: 'Select waiver',
              required: true,
            },
          ],
          [
            {
              inputType: 'number',
              label: 'Minimum Age',
              name: 'minimumAge',
              placeholder: 'Enter minimum age',
              min: 0,
            },
            {
              inputType: 'select',
              label: 'Age Unit',
              name: 'minimumAgeUnit',
              options: [
                { label: 'Months', value: 'months' },
                { label: 'Years', value: 'years' },
              ],
              autoWidth: false,
            },
          ],
          [
            {
              inputType: 'text',
              label: 'Aftercare Link',
              name: 'aftercareLink',
              placeholder: 'Enter aftercare link',
              tooltip: TOOLTIP.aftercareLink,
            },
          ],
        ];
        return inputRows;
      },
      onSubmit: async values => {
        const isWaiverPublished = waiversData?.options?.some(
          option =>
            option.waiverName === values.waiverName &&
            option.status === 'PUBLISHED',
        );
        const isSelectedWaiverUpdated =
          !serviceToUpdate?.waiver ||
          serviceToUpdate?.waiver?.name !== values.waiverName;

        if (
          values.waiverName &&
          !isWaiverPublished &&
          isSelectedWaiverUpdated
        ) {
          const confirmed = await confirm({
            title: 'Waiver not published',
            message:
              'The waiver you selected has not been published. In order for the selected waiver to be used, it must be published. If you continue, you can publish the waiver later and it will be automatically applied to this service.',
            positiveText: 'Continue',
            negativeText: 'Cancel',
          });
          if (!confirmed) return;
        }

        const isCategoryChanged =
          serviceToUpdate &&
          serviceToUpdate.serviceCategory !== values.serviceCategory;
        if (isCategoryChanged && serviceToUpdate?._count.appointments) return;

        const response = await handleUpsertBusinessService(
          user.business.id,
          serviceToUpdate ?? null,
          values,
        );
        if (response) {
          postMessageToReactNative('services-changed');
          reloadAfterUpdate('SHOP_SETTINGS');
          await refetch();
          setForm(null);
        }
      },
      submitButtonText: serviceToUpdate ? 'Update Service' : 'Create Service',
      title: serviceToUpdate ? 'Edit Service' : 'Create Service',
      validationSchema: upsertBusinessServiceSchema,
    };
  };

  const rows: SettingsRow[] = isLoading
    ? [
        {
          id: 'loading',
          component: (
            <Box
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="center">
              <CircularProgress color="primary" />
            </Box>
          ),
        },
      ]
    : !services?.length
    ? [
        {
          id: 'empty',
          component: (
            <Typography variant="body2">
              Add any non-tattoo services that you offer here, like Piercing,
              PMU, Tooth Gems, etc.
            </Typography>
          ),
        },
      ]
    : services.map(service => ({
        id: service.serviceId.toString(),
        component: <ServiceRow key={service.serviceId} service={service} />,
        action: {
          type: 'button',
          text: 'Edit',
          onClick: () => setForm(getServiceForm(service)),
          disabled: false,
        },
      }));

  return (
    <>
      <SettingsSection
        title="Other Services"
        subtitle="Add any non-tattoo services that you offer here, like Piercing, PMU, Tooth Gems, etc."
        rows={rows}
        action={{
          type: 'button',
          text: 'Add New Service',
          onClick: () => setForm(getServiceForm()),
          disabled: false,
        }}
        copy={
          services?.length
            ? {
                content: getBusinessShopUrl(user?.business),
              }
            : undefined
        }
      />
      <FormSmallModal form={form} onClose={() => setForm(null)} open={!!form} />
    </>
  );
}

export default ServicesSettings;
